import { Slot } from 'radix-ui';
import React, { type ComponentPropsWithRef, type ReactNode } from 'react';
import { cx } from '@mildang/styled-system/css';
import { Spinner } from '../Spinner';
import type { SpinnerColor } from './ButtonBase';

export interface IconButtonBaseProps
  extends Omit<ComponentPropsWithRef<'button'>, 'ref' | 'className'> {
  asChild?: boolean;
  loading?: boolean;
  loadingColor?: SpinnerColor;
  loadingSize?: number;
  rootClassName: string;
  /**
   * 호출자가 splitCssProps로 panda style props를 처리한 결과의 className을 전달한다.
   * @remarks asChild=true 사용 시 ref는 자식 element를 가리킨다.
   * ref 타입은 HTMLButtonElement로 단언되지만 실제 element가 다를 수 있으므로
   * button 전용 API(form 매칭 등) 사용은 피한다.
   */
  className?: string;
  children?: ReactNode;
}

export const IconButtonBase = React.forwardRef<HTMLButtonElement, IconButtonBaseProps>(
  (props, forwardedRef) => {
    const {
      asChild = false,
      loading = false,
      loadingColor = 'neutral',
      loadingSize,
      rootClassName,
      className,
      children,
      type = 'button',
      disabled = false,
      ...rest
    } = props;

    const Comp = asChild ? Slot.Root : 'button';

    return (
      <Comp
        ref={forwardedRef}
        type={asChild ? undefined : type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        data-loading={loading ? 'true' : undefined}
        {...rest}
        className={cx(rootClassName, className)}
      >
        <Slot.Slottable>
          {loading ? <Spinner color={loadingColor} size={loadingSize} /> : children}
        </Slot.Slottable>
      </Comp>
    );
  },
);

IconButtonBase.displayName = 'IconButtonBase';
